import React, { useCallback, useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Typography } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import EmptyState from 'common/components/EmptyState';
import { ReactHookFormIntlNumberInput } from 'common/components/form/bootstrap';
import { IconEmptyGroups } from 'common/components/icons/index.jsx';
import { validateGroupsDimensions } from 'common/components/segment/helpers';
import {
    getMinCol,
    getMinMaxCols,
    getMinMaxRows,
    getMinRow,
} from 'common/utils/helpers/systemsValues';

import IsValidGroup from './IsValidGroup';

const CFEGroupsListArray = ({
    control,
    disabled,
    name,
    orientation,
    panel,
    roofTypes,
    setHasConfigErrors,
    setInitialValues,
    system_uid,
    systemName,
    type_application,
    watch,
    segments,
    selectedSegmentId,
    setHandleAppendRef,
}) => {
    const { t } = useTranslation();
    const { append, fields, remove } = useFieldArray({ control, name });
    const watchFieldArray = watch(name);
    const roofType = roofTypes.find((type) => type.key === type_application);
    const roofTypeName = roofType ? roofType.name : '';
    const [minCols, maxCols] = getMinMaxCols(system_uid, panel?.width);
    const [minRows, maxRows] = getMinMaxRows(system_uid);
    const controlledFields = fields.map((field, index) => ({
        ...field,
        ...watchFieldArray[index],
    }));
    const validatedGroupsDimensions = validateGroupsDimensions({
        groups: controlledFields,
        maxCols,
        maxRows,
        minCols,
        minRows,
    });
    const validatedGroups = validatedGroupsDimensions;
    const hasError = validatedGroups.some((g) =>
        Object.values(g.valid).some((v) => v === false),
    );

    const disabledCols = minCols === maxCols;
    const disabledRows = minRows === maxRows;

    const handleAppend = useCallback(() => {
        const doAppend = () =>
            append({
                col: getMinCol(system_uid),
                row: getMinRow(system_uid),
                match: 1,
            });
        if (disabled) {
            const r = setInitialValues(
                segments.filter((s) => s.id === selectedSegmentId)[0],
            );
            if (r && typeof r.then === 'function') {
                r.then(() => setTimeout(doAppend, 0));
            } else {
                setTimeout(doAppend, 0);
            }
        } else {
            doAppend();
        }
    }, [
        append,
        disabled,
        segments,
        selectedSegmentId,
        setInitialValues,
        system_uid,
    ]);

    useEffect(() => {
        setHasConfigErrors(hasError);
    }, [hasError]);

    useEffect(() => {
        if (setHandleAppendRef) {
            setHandleAppendRef(() => handleAppend);
        }
    }, [handleAppend, setHandleAppendRef]);

    return (
        <>
            {!validatedGroups.length && (
                <EmptyState
                    srcSvg={<IconEmptyGroups />}
                    sxCard={{ border: 0, boxShadow: 'none', mt: 5 }}
                    title={t('No groups added yet')}
                    onClick={handleAppend}
                    textButton={t('Add')}
                    caption={t(
                        'Start editing your panel groups by clicking Add',
                    )}
                />
            )}
            {validatedGroups.map((item, i) => {
                const count = item.row * item.col * item.match;
                const isValid = item.valid.dimension ? true : false;
                const orientationLabel =
                    orientation === 'L' ? t('Landscape') : t('Portrait');
                return (
                    <Box
                        key={item.id}
                        sx={{
                            backgroundColor: !isValid
                                ? '#ffcfcf'
                                : 'transparent',
                            p: 1,
                        }}
                    >
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid size={{ md: 3, xs: 6 }}>{roofTypeName}</Grid>
                            <Grid size={{ md: 3, xs: 6 }}>{systemName}</Grid>
                            <Grid size={{ md: 2.5, xs: 6 }}>
                                <Typography>{orientationLabel}</Typography>
                            </Grid>
                            <Grid size={{ md: 2, xs: 6 }}>
                                <ReactHookFormIntlNumberInput
                                    control={control}
                                    disabled={disabled || disabledRows}
                                    label={t('Row', { count: 2 })}
                                    name={`${name}.${i}.row`}
                                />
                            </Grid>
                            <Grid size={{ md: 2, xs: 6 }}>
                                <ReactHookFormIntlNumberInput
                                    control={control}
                                    disabled={disabled || disabledCols}
                                    label={t('Column', { count: 2 })}
                                    name={`${name}.${i}.col`}
                                />
                            </Grid>
                            <Grid size={{ md: 2, xs: 6 }}>
                                <ReactHookFormIntlNumberInput
                                    control={control}
                                    disabled={disabled}
                                    label={t('Repetitions')}
                                    name={`${name}.${i}.match`}
                                />
                            </Grid>
                            <Grid size={{ md: 2, xs: 6 }} sx={{ ml: 4 }}>
                                {count} {t('Panel', { count })}
                            </Grid>

                            <Grid size={{ md: 0.5, xs: 6 }}>
                                <IconButton
                                    disabled={disabled}
                                    onClick={() => remove(i)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <IsValidGroup
                            isValid={isValid}
                            item={item}
                            maxCols={maxCols}
                            maxRows={maxRows}
                            minCols={minCols}
                            minRows={minRows}
                        />
                    </Box>
                );
            })}
        </>
    );
};

CFEGroupsListArray.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    orientation: PropTypes.string,
    panel: PropTypes.object,
    roofTypes: PropTypes.array,
    setHasConfigErrors: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    setInitialValues: PropTypes.func,
    setHandleAppendRef: PropTypes.func,
    system_uid: PropTypes.string,
    systemName: PropTypes.string,
    type_application: PropTypes.string,
    watch: PropTypes.func,
};

export default CFEGroupsListArray;
